import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, Table, Tag, Button, Drawer, Typography, Space, Input, message,
  Popconfirm, Spin, Empty, Alert,
} from 'antd';
import {
  ReloadOutlined, DeleteOutlined, RedoOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { trendsAPI } from '../services/api';

const { Title, Text } = Typography;

const STATUS_COLOR = {
  done: 'success', running: 'processing', failed: 'error', queued: 'default',
};

export default function TrendsReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [drawerId, setDrawerId] = useState(searchParams.get('id') ? Number(searchParams.get('id')) : null);
  const [drawerReport, setDrawerReport] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trendsAPI.listReports({ limit: 100 });
      setReports((res.data ?? res).reports || []);
    } catch (e) {
      message.error(`載入失敗：${e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (drawerId == null) { setDrawerReport(null); return; }
    setDrawerLoading(true);
    trendsAPI.getReport(drawerId)
      .then(res => setDrawerReport((res.data ?? res).report))
      .catch(e => message.error(`載入報告失敗：${e.message}`))
      .finally(() => setDrawerLoading(false));
  }, [drawerId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter(r =>
      (r.topic || '').toLowerCase().includes(q) ||
      (r.summary || '').toLowerCase().includes(q)
    );
  }, [reports, search]);

  const handleDelete = async (id) => {
    try {
      await trendsAPI.deleteReport(id);
      message.success('已刪除');
      fetchAll();
    } catch (e) { message.error(`刪除失敗：${e.message}`); }
  };
  const handleRetry = async (id) => {
    try {
      await trendsAPI.retryReport(id);
      message.success('已重新排入佇列');
      fetchAll();
    } catch (e) { message.error(`重試失敗：${e.message}`); }
  };

  const columns = [
    {
      title: '狀態', dataIndex: 'status', width: 100,
      render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>,
    },
    {
      title: '主題', dataIndex: 'topic',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          {r.summary && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.summary.slice(0, 80)}{r.summary.length > 80 ? '...' : ''}
            </Text>
          )}
          {r.error && <Text type="danger" style={{ fontSize: 12, display: 'block' }}>❌ {r.error}</Text>}
        </div>
      ),
    },
    {
      title: '競品', dataIndex: 'competitors', width: 70, align: 'center',
      render: v => v ? <Tag>✓</Tag> : '—',
    },
    {
      title: '耗時', dataIndex: 'run_seconds', width: 80, align: 'center',
      render: v => v ? `${v}s` : '—',
    },
    {
      title: '時間', dataIndex: 'created_at', width: 150,
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '—',
    },
    {
      title: '動作', width: 200,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<FileTextOutlined />} onClick={() => {
            setDrawerId(r.id);
            setSearchParams({ id: String(r.id) });
          }}>看報告</Button>
          {r.status === 'failed' && (
            <Button size="small" icon={<RedoOutlined />} onClick={() => handleRetry(r.id)}>重試</Button>
          )}
          <Popconfirm title="刪除這份報告？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <FileTextOutlined /> 研究報告庫
          </Title>
          <Text type="secondary">所有 last30days-skill 跑過的研究報告</Text>
        </div>
        <Space>
          <Input.Search placeholder="搜尋主題 / 摘要" allowClear
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: 240 }} />
          <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>重新整理</Button>
        </Space>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="middle"
        />
      </Card>

      <Drawer
        title={drawerReport?.topic || '報告詳情'}
        open={!!drawerId}
        onClose={() => { setDrawerId(null); setSearchParams({}); }}
        width="60%"
      >
        {drawerLoading ? <Spin size="large" /> : !drawerReport ? <Empty /> : (
          <div>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag color={STATUS_COLOR[drawerReport.status]}>{drawerReport.status}</Tag>
              {drawerReport.competitors && <Tag>競品比較</Tag>}
              {drawerReport.run_seconds && <Tag>{drawerReport.run_seconds}s</Tag>}
              <Text type="secondary">
                {drawerReport.created_at && new Date(drawerReport.created_at).toLocaleString('zh-TW')}
              </Text>
            </Space>

            {drawerReport.summary && (
              <Alert
                type="info" showIcon
                style={{ marginBottom: 12 }}
                message="摘要"
                description={drawerReport.summary}
              />
            )}

            {drawerReport.sources_used && drawerReport.sources_used.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>使用的來源：</Text>
                <Space wrap style={{ marginLeft: 8 }}>
                  {drawerReport.sources_used.map((s, i) => <Tag key={i}>{s}</Tag>)}
                </Space>
              </div>
            )}

            {drawerReport.content ? (
              drawerReport.emit === 'html' ? (
                <iframe
                  srcDoc={drawerReport.content}
                  title="report"
                  style={{ width: '100%', height: 'calc(100vh - 280px)', border: '1px solid #e5e7eb', borderRadius: 8 }}
                />
              ) : (
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f9fafb', padding: 12, borderRadius: 8, maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
                  {drawerReport.content}
                </pre>
              )
            ) : drawerReport.status === 'running' ? (
              <Spin tip="報告產出中，請稍候..." />
            ) : (
              <Empty description="沒有報告內容（可能 file_path 取不回來）" />
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
